import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  FlatList,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { useStrategyOrganizationsQuery, useOrganizationGoalsQuery } from '../../hooks/queries';
import { OrganizationList, Organization, StrategicGoalList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb, EmptyState } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { OrganizationCard } from './components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { formatPct } from '../../utils/formatPercentage';

import { tokens } from '@design';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Back Arrow Icon
const ArrowRightIcon = ({ size = 24, color = tokens.colors.raw.gray["900"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Search Icon
const SearchIcon = ({ size = 20, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Plus Icon
const PlusIcon = ({ size = 20, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Building Icon
const BuildingIcon = ({ size = 24, color = tokens.colors.semantic.general["Main Color"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Progress Circle Component
const ProgressCircle = ({
  percentage,
  size = 60,
  strokeWidth = 6
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

  const getColor = () => {
    if (safePercentage >= 80) return tokens.colors.raw.green["500"];
    if (safePercentage >= 50) return tokens.colors.raw.orange["500"];
    return tokens.colors.raw.red["500"];
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </Svg>
      <Text style={[styles.progressText, { color: getColor() }]}>
        %{formatPct(safePercentage)}
      </Text>
    </View>
  );
};

// ChevronLeft Icon
const ChevronLeftIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Extended Organization type with additional fields
interface ExtendedOrganization extends OrganizationList {
  vision_achievement?: number;
  commitment_index?: number;
  kpis_count?: number;
}

export default function OrganizationsListScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    headerBg: colors.card,
    headerBorder: colors.cardBorder,
    headerTitle: colors.textPrimary,
    headerSubtitle: colors.textMuted,
    searchBg: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"],
    searchInput: colors.textPrimary,
    backButtonBg: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"],
    orgName: colors.textPrimary,
    statLabel: colors.textMuted,
    countContainer: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["100"],
    countValue: colors.textPrimary,
    goalsSectionBg: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["50"],
    goalsSectionTitle: colors.textSecondary,
    loadingText: colors.textMuted,
    goalCard: colors.card,
    goalTitle: colors.textPrimary,
    goalProgress: colors.textSecondary,
    iconContainer: isDark ? colors.primaryLight : tokens.colors.raw.red["50"],
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);

  // Organizations list — React Query cache keyed by year. Repeat visits
  // within 10min staleTime render from cache with no spinner flash.
  const {
    data: orgsData = [],
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = useStrategyOrganizationsQuery(selectedYear);
  const organizations = orgsData as ExtendedOrganization[];

  // Goals for the expanded org. React Query caches per (orgId, year) for free
  // — collapse + re-expand the same org resolves from cache, no HTTP call.
  const {
    data: orgGoalsData = [],
    isPending: goalsQueryPending,
    isFetching: goalsQueryFetching,
  } = useOrganizationGoalsQuery(selectedOrgId, selectedYear);
  const orgGoals = selectedOrgId ? orgGoalsData : [];
  // Only show the goals spinner when we're actually fetching (not just
  // waiting for `enabled` to flip). `isPending` is true while disabled so
  // we gate on isFetching + a selected org.
  const loadingGoals = selectedOrgId != null && goalsQueryFetching && goalsQueryPending;

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSelectOrg = useCallback((orgId: number) => {
    setSelectedOrgId(prev => (prev === orgId ? null : orgId));
  }, []);

  const handleCreateGoal = (orgId: number) => {
    navigation.navigate('AddGoal', { organizationId: orgId });
  };

  const handleViewGoal = (goalId: number, org: ExtendedOrganization) => {
    navigation.navigate('GoalDetail', {
      id: goalId,
      organizationId: org.id,
      organizationName: translateOrg(org.name),
      orgNavSource: 'strategy',
    });
  };

  const handleEditGoal = (goalId: number) => {
    navigation.navigate('AddGoal', { goalId });
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: 'الرئيسية' },
          { label: 'الجهات الحكومية' },
        ]}
      />

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>الجهات الحكومية</Text>
          <Text style={styles.headerSubtitle}>
            {organizations.length} جهة حكومية
          </Text>
        </View>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowRightIcon size={24} color={tokens.colors.raw.gray["900"]} />
        </TouchableOpacity>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <SearchIcon size={20} color={tokens.colors.raw.gray["400"]} />
          <TextInput
            style={styles.searchInput}
            placeholder="البحث عن جهة..."
            placeholderTextColor={tokens.colors.raw.gray["400"]}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>
      </View>

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {filteredOrganizations.length === 0 ? (
          <EmptyState
            title="لا توجد جهات"
            message={searchQuery ? 'لم يتم العثور على نتائج للبحث' : 'لم يتم إضافة أي جهات بعد'}
          />
        ) : (
          filteredOrganizations.map((org) => (
            <View key={org.id}>
              {/* Organization Card */}
              <TouchableOpacity
                style={[styles.orgCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                onPress={() => handleSelectOrg(org.id)}
                activeOpacity={0.7}
              >
                {/* Header */}
                <View style={styles.orgHeader}>
                  <View style={[styles.orgIconContainer, { backgroundColor: themeColors.iconContainer }]}>
                    <BuildingIcon size={24} color={colors.primary} />
                  </View>
                  <View style={styles.orgHeaderContent}>
                    <Text style={[styles.orgName, { color: themeColors.orgName }]} numberOfLines={2}>{translateOrg(org.name)}</Text>
                  </View>
                  <ChevronLeftIcon
                    size={20}
                    color={selectedOrgId === org.id ? colors.primary : colors.iconDefault}
                  />
                </View>

                {/* Stats Row */}
                <View style={[styles.statsRow, { borderTopColor: colors.border }]}>
                  {/* Vision Achievement */}
                  <View style={styles.statItem}>
                    <ProgressCircle percentage={Number(((org.stage_actual_progress ?? 0) * 100).toFixed(2))} size={56} strokeWidth={5} />
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>تحقيق الرؤية</Text>
                  </View>

                  {/* Commitment Index */}
                  <View style={styles.statItem}>
                    <ProgressCircle percentage={org.commitment_index ?? 0} size={56} strokeWidth={5} />
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>مؤشر الالتزام</Text>
                  </View>

                  {/* Goals Count */}
                  <View style={styles.statItem}>
                    <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
                      <Text style={[styles.countValue, { color: themeColors.countValue }]}>{org.strategic_goals_count ?? 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>الأهداف</Text>
                  </View>

                  {/* KPIs Count */}
                  <View style={styles.statItem}>
                    <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
                      <Text style={[styles.countValue, { color: themeColors.countValue }]}>{org.kpis_count ?? 0}</Text>
                    </View>
                    <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>المؤشرات</Text>
                  </View>
                </View>
              </TouchableOpacity>

              {/* Expanded Goals Section */}
              {selectedOrgId === org.id && (
                <View style={[styles.goalsSection, { backgroundColor: themeColors.goalsSectionBg, borderColor: themeColors.cardBorder }]}>
                  <View style={styles.goalsSectionHeader}>
                    <TouchableOpacity
                      style={styles.addGoalButton}
                      onPress={() => handleCreateGoal(org.id)}
                    >
                      <PlusIcon size={16} color={tokens.colors.raw.white} />
                      <Text style={styles.addGoalButtonText}>إضافة هدف</Text>
                    </TouchableOpacity>
                    <Text style={[styles.goalsSectionTitle, { color: themeColors.goalsSectionTitle }]}>
                      أهداف الجهة ({orgGoals.length})
                    </Text>
                  </View>

                  {loadingGoals ? (
                    <View style={styles.loadingContainer}>
                      <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>جاري التحميل...</Text>
                    </View>
                  ) : orgGoals.length === 0 ? (
                    <View style={[styles.noGoalsContainer, { backgroundColor: themeColors.goalCard }]}>
                      <Text style={[styles.noGoalsText, { color: themeColors.loadingText }]}>لا توجد أهداف لهذه الجهة</Text>
                    </View>
                  ) : (
                    orgGoals.map((goal) => (
                      <View key={goal.id} style={[styles.goalCard, { backgroundColor: themeColors.goalCard, borderColor: themeColors.cardBorder }]}>
                        <View style={styles.goalContent}>
                          <Text style={[styles.goalTitle, { color: themeColors.goalTitle }]} numberOfLines={2}>
                            {goal.title}
                          </Text>
                          <View style={styles.goalMeta}>
                            <View style={[
                              styles.goalStatus,
                              goal.status === 'approved' && styles.goalStatusApproved,
                              goal.status === 'pending' && styles.goalStatusPending,
                              goal.status === 'rejected' && styles.goalStatusRejected,
                            ]}>
                              <Text style={[
                                styles.goalStatusText,
                                goal.status === 'approved' && styles.goalStatusTextApproved,
                                goal.status === 'pending' && styles.goalStatusTextPending,
                                goal.status === 'rejected' && styles.goalStatusTextRejected,
                              ]}>
                                {goal.status_display ?? goal.status}
                              </Text>
                            </View>
                            <Text style={styles.goalProgress}>
                              %{formatPct(goal.achievement_percentage ?? 0)}
                            </Text>
                          </View>
                        </View>
                        <View style={styles.goalActions}>
                          <TouchableOpacity
                            style={styles.goalActionButton}
                            onPress={() => handleViewGoal(goal.id, org)}
                          >
                            <Text style={styles.goalActionText}>عرض</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.goalActionButton, styles.goalEditButton]}
                            onPress={() => handleEditGoal(goal.id)}
                          >
                            <Text style={styles.goalEditText}>تعديل</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))
                  )}
                </View>
              )}
            </View>
          ))
        )}
      </ScrollView>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  headerContent: {
    flex: 1,
    marginEnd: tokens.spacing.absolute["3"],
  },
  headerTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
  },
  headerSubtitle: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["0_5"],
  },
  searchContainer: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-10"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    height: 44,
    gap: tokens.spacing.absolute["2"],
  },
  searchInput: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["4"],
    paddingBottom: tokens.spacing.absolute["8"],
  },
  orgCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["3"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  orgHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  orgIconContainer: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.red["50"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  orgHeaderContent: {
    flex: 1,
    marginEnd: tokens.spacing.absolute["3"],
    marginStart: tokens.spacing.absolute["2"],
  },
  orgName: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["100"],
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["1_5"],
    textAlign: 'center',
  },
  progressText: {
    position: 'absolute',
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '600',
  },
  countContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: tokens.colors.raw.gray["100"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  countValue: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: tokens.colors.raw.gray["700"],
  },
  goalsSection: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["3"],
    marginTop: -8,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderTopWidth: 0,
    borderTopStartRadius: 0,
    borderTopEndRadius: 0,
  },
  goalsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["3"],
  },
  goalsSectionTitle: {
    fontSize: 15,
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["700"],
  },
  addGoalButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["1_5"],
  },
  addGoalButtonText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  loadingContainer: {
    paddingVertical: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  loadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  noGoalsContainer: {
    paddingVertical: tokens.spacing.absolute["6"],
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  noGoalsText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  goalCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["2"],
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
  },
  goalContent: {
    flex: 1,
    marginEnd: tokens.spacing.absolute["3"],
  },
  goalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1_5"],
  },
  goalMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  goalStatus: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  goalStatusApproved: {
    backgroundColor: '#DCFCE7',
  },
  goalStatusPending: {
    backgroundColor: tokens.colors.raw.amber["100"],
  },
  goalStatusRejected: {
    backgroundColor: tokens.colors.raw.red["50"],
  },
  goalStatusText: {
    fontSize: 10,
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["500"],
  },
  goalStatusTextApproved: {
    color: '#16A34A',
  },
  goalStatusTextPending: {
    color: '#D97706',
  },
  goalStatusTextRejected: {
    color: tokens.colors.raw.red["600"],
  },
  goalProgress: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: tokens.colors.raw.gray["700"],
  },
  goalActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["1_5"],
  },
  goalActionButton: {
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  goalEditButton: {
    backgroundColor: '#EFF6FF',
  },
  goalActionText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  goalEditText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: '#2563EB',
  },
});

const styles = getStyles(true);
